import { Link } from "@tanstack/react-router";
import {
  BookOpen,
  GraduationCap,
  Library,
  type LucideIcon,
  Sparkles,
  Star,
} from "lucide-react";
import { ROUTES } from "#/lib/routes";
import { SUPPLIER_LOGOS } from "#/lib/supplier-logos";
import { useReveal } from "#/lib/useReveal";

type SatelliteSize = "sm" | "md" | "lg";

type SatelliteSlot = {
  name: string;
  Icon: LucideIcon;
  logoSrc?: string;
  dark?: boolean;
  size: SatelliteSize;
  top: string;
  left: string;
  publisher: string;
};

const INTERNATIONAL_PARTNERS: ReadonlyArray<SatelliteSlot> = [
  {
    name: "Total Language Plus",
    Icon: BookOpen,
    logoSrc: SUPPLIER_LOGOS.totalLanguagePlus,
    size: "md",
    top: "13%",
    left: "26%",
    publisher: "total-language-plus",
  },
  {
    name: "MasterBooks",
    Icon: Library,
    logoSrc: SUPPLIER_LOGOS.masterBooks,
    dark: true,
    size: "md",
     top: "16%",
    left: "53%",
    publisher: "masterbooks",
  },
  {
    name: "Apologia",
    Icon: GraduationCap,
    logoSrc: SUPPLIER_LOGOS.apologia,
    size: "md",
    top: "23%",
    left: "78%",
    publisher: "apologia",
  },
  {
    name: "AOP",
    Icon: Sparkles,
    logoSrc: SUPPLIER_LOGOS.aop,
    size: "sm",
    top: "30%",
    left: "35%",
    publisher: "alpha-omega-publications",
  },
  {
    name: "BJU Press",
    Icon: Star,
    logoSrc: SUPPLIER_LOGOS.bjuPress,
    size: "sm",
    top: "37%",
    left: "66%",
    publisher: "bju-press",
  },
  {
    name: "Living Book Press",
    Icon: BookOpen,
    logoSrc: SUPPLIER_LOGOS.livingBooks,
    size: "lg",
    top: "53%",
    left: "24%",
    publisher: "living-book-press",
  },
  {
    name: "Star Publishing",
    Icon: Star,
    logoSrc: SUPPLIER_LOGOS.starPublishing,
    size: "lg",
    top: "64%",
    left: "75%",
    publisher: "star-publishing",
  },
  {
    name: "Tyndale",
    Icon: BookOpen,
    logoSrc: SUPPLIER_LOGOS.tyndale,
    size: "lg",
    top: "82%",
    left: "30%",
    publisher: "tyndale-house-publishers",
  },
  {
    name: "Bright Ideas Press",
    Icon: Sparkles,
    logoSrc: SUPPLIER_LOGOS.brightIdeasPress,
    size: "sm",
    top: "84%",
    left: "46%",
    publisher: "bright-ideas-press",
  },
  {
    name: "Classical Conversations",
    Icon: GraduationCap,
    logoSrc: SUPPLIER_LOGOS.classicalConversations,
    size: "lg",
    top: "84%",
    left: "62%",
    publisher: "classical-conversations",
  },
]

const SATELLITE_SIZES: Record<SatelliteSize, string> = {
  sm: "size-32",
  md: "size-40",
  lg: "size-48",
};

function SatelliteFigure(
  { slot, revealed } 
  : {
  slot: SatelliteSlot;
  revealed: boolean;
  }
) {
  const dark = slot.dark ?? false;
  const bg = dark ? "bg-lp-green-dk" : "bg-white";
  const labelColor = dark ? "text-white" : "text-lp-green-dk";
  const iconColor = dark ? "text-white" : "text-lp-green-dk";

  const cardClass = `absolute flex flex-col items-center justify-center gap-1 rounded-full p-3 no-underline ${bg} ${SATELLITE_SIZES[slot.size]}`;

  const cardStyle = {
    top: revealed ? slot.top : "50%",
    left: revealed ? slot.left : "50%",
    transform: revealed
       ? "translate(-50%, -50%) scale(1)"
      : "translate(-50%, -50%) scale(0.3)",
    opacity: revealed ? 1 : 0,
     transition: [
      `top 1200ms cubic-bezier(0.22, 1, 0.36, 1)`,
      `left 1200ms cubic-bezier(0.22, 1, 0.36, 1)`,
      `transform 1200ms cubic-bezier(0.22, 1, 0.36, 1)`,
      `opacity 300ms ease-out`,
    ].join(", "),
  };

  const inner = slot.logoSrc ? (
    <img
      src={slot.logoSrc}
      alt={slot.name}
      loading="lazy"
      decoding="async"
      className="rounded-full"
    />
  ) : (
    <>
      <slot.Icon className={`size-6 ${iconColor}`} strokeWidth={2} />
      <span className={`px-1 text-center font-bold font-display text-xs leading-tight ${labelColor}`}>
        {slot.name}
      </span>
    </>
  );

  return (
    <Link
      to={ROUTES.shop}
      search={{ publisher: [slot.publisher], page: 1, sort: "default" }}
      aria-label={`Shop ${slot.name}`}
      className={cardClass}
      style={cardStyle}
    >
      {inner}
    </Link>
  );
}

function PartnerCard({
  slot,
  index,
  revealed,
}: {
  slot: SatelliteSlot;
  index: number;
  revealed: boolean;
}) {
  const dark = slot.dark ?? false;
  const bg = dark ? "bg-lp-green-dk" : "bg-white";
  const labelColor = dark ? "text-white" : "text-lp-green-dk";
  const iconColor = dark ? "text-white" : "text-lp-green-dk";

  const cardClass = `flex aspect-square flex-col items-center justify-center gap-1 rounded-full border-2 border-lp-green-mid p-2 no-underline ${bg}`;
  const cardStyle = {
    opacity: revealed ? 1 : 0,
    transform: revealed ? "scale(1)" : "scale(0.6)",
    transition:
      "opacity 500ms ease-out, transform 600ms cubic-bezier(0.22, 1, 0.36, 1)",
    transitionDelay: `${index * 60}ms`,
  };
  const inner = slot.logoSrc ? (
    <img
      src={slot.logoSrc}
      alt={slot.name}
      loading="lazy"
      decoding="async"
      className="w-full rounded-full"
    />
  ) : (
    <>
      <slot.Icon className={`size-4 ${iconColor}`} strokeWidth={2} />
      <span
        className={`px-1 text-center font-bold font-display text-[10px] leading-tight ${labelColor}`}
      >
        {slot.name}
      </span>
    </>
  );
  return (
    <Link
      to={ROUTES.shop}
      search={{ publisher: [slot.publisher], page: 1, sort: "default" }}
      aria-label={`Shop ${slot.name}`}
      className={cardClass}
      style={cardStyle}
    >
      {inner}
    </Link>
  );
}

export function TrustedPartners() {
  const { ref, revealed } = useReveal<HTMLElement>();

  return (
    <section ref={ref} className="pt-12 pb-8">
      <div className="mx-auto w-full max-w-figma px-6 md:px-10">
        <h2 className="m-0 mb-2 text-center font-display font-bold text-3xl sm:text-4xl md:text-5xl leading-tight md:leading-none text-[#67815F]">
          OUR TRUSTED PARTNERS
        </h2>
      </div>

       <div className="mx-auto w-full max-w-figma px-6 md:hidden">
        <div className="mt-8 rounded-3xl bg-lp-green px-5 pt-10 pb-8">
          <div className="mx-auto flex size-[200px] items-center justify-center rounded-full bg-white">
            <div className="flex size-[178px] items-center justify-center rounded-full border-[5px] border-lp-green-mid">
              <span className="text-center font-bold font-display text-base text-lp-green-dk leading-tight tracking-wide">
                INTERNATIONAL
                <br />
                PARTNERS
              </span>
            </div>
          </div>
          <div className="mt-8 grid grid-cols-3 gap-3">
            {INTERNATIONAL_PARTNERS.map((slot, idx) => (
              <PartnerCard
                key={slot.name}
                slot={slot}
                index={idx}
                revealed={revealed}
              />
            ))}
          </div>
        </div>
        </div>
      


      <div className="hidden md:block relative mt-12 aspect-[16/9] w-full overflow-hidden rounded-[40px]">
        <div
          className="absolute top-1/2 left-1/2 bg-lp-green"
          style={{
             width: revealed ? "3000px" : "330px",
            height: revealed ? "3000px" : "330px",
            borderRadius: "50%",
            transform: "translate(-50%, -50%)",
            transition:
              "width 1300ms cubic-bezier(0.4, 0, 1, 1), height 1300ms cubic-bezier(0.4, 0, 1, 1)",
          }}
        />

        <div className="absolute top-1/2 left-1/2 z-10 flex size-[330px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white">
          <div className="flex size-[290px] items-center justify-center rounded-full border-[6px] border-lp-green-mid">
            <span className="text-center font-bold font-display text-2xl text-lp-green-dk leading-tight tracking-wide">
              INTERNATIONAL
              <br />
              PARTNERS
            </span>
          </div>
        </div>
        <div
          className="absolute inset-0"
          style={{
            transform: revealed ? "rotate(0deg)" : "rotate(25deg)",
            transformOrigin: "50% 50%",
            transition: "transform 1200ms cubic-bezier(0.34, 1.2, 0.64, 1)",
          }}
        >
          {INTERNATIONAL_PARTNERS.map((slot) => (
            <SatelliteFigure
              key={slot.name}
              slot={slot}
              revealed={revealed}
            />
          ))}
        </div>

      </div>
    </section>
  );
}
